import { Button, Divider, Group, SimpleGrid, Stack, TextInput, Title, Tooltip } from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconTrash } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { DevtoolCard } from "routes/_auth/_dev/devtools";
import { Content } from "shared/components/global/Content";
import { useCustomer } from "shared/graphql/cust";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { runAsync, voiding } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import z from "zod";

const Page = () => {
    const isMobile = useIsMobile();

    function openEditBlackoutPeriod() {
        modals.open({
            title: <Title order={3}>Blackout Period</Title>,
            children: <EditBlackoutPeriodPanel />,
        });
    }

    function openEditDynamicTemplateId() {
        modals.open({
            title: <Title order={3}>Dynamic Template Id</Title>,
            children: <EditDynamicTemplateId />,
        });
    }

    return (
        <Content>
            <Content.Heading backable>Customer Devtools</Content.Heading>
            <SimpleGrid cols={isMobile ? 1 : 2}>
                <Link to="/devtools/customer/features">
                    <DevtoolCard>Features</DevtoolCard>
                </Link>

                <DevtoolCard onClick={openEditBlackoutPeriod}>Blackout Period</DevtoolCard>

                <Link to="/devtools/customer/numbers">
                    <DevtoolCard>Phone Numbers</DevtoolCard>
                </Link>

                <DevtoolCard onClick={openEditDynamicTemplateId}>Dynamic Template Id</DevtoolCard>
            </SimpleGrid>
        </Content>
    );
};

const EditDynamicTemplateId = () => {
    const form = useForm<{ id: string }>({
        initialValues: { id: "" },
        validate: { id: (val) => (!val.startsWith("d-") ? "Valid IDs begin with 'd-'" : null) },
    });

    const setDynamicTemplateId = useGraphqlMutation(muts.setDynamicTemplateId);

    return (
        <form
            onSubmit={form.onSubmit(({ id }) => {
                runAsync(async () => {
                    await setDynamicTemplateId.mutateAsync({ id });
                    notify.show.success("Updated dynamic template id");
                    modals.closeAll();
                });
            })}
        >
            <Stack>
                <TextInput label="ID" {...form.getInputProps("id")} />
                <Divider mx="-md" />
                <Group justify="space-between">
                    <Tooltip
                        label="Removes the ID from this customer, email alerts will be sent as is and without an unsubscribe link"
                        multiline
                        maw={400}
                    >
                        <Button
                            variant="outline"
                            leftSection={<IconTrash size={16} />}
                            onClick={() =>
                                runAsync(async () => {
                                    await setDynamicTemplateId.mutateAsync({ id: null });
                                    notify.show.success("Removed dynamic template id");
                                    modals.closeAll();
                                })
                            }
                        >
                            Clear
                        </Button>
                    </Tooltip>
                    <Group>
                        <Button variant="default">Cancel</Button>
                        <Button type="submit" loading={setDynamicTemplateId.isPending}>
                            Save
                        </Button>
                    </Group>
                </Group>
            </Stack>
        </form>
    );
};

const EditBlackoutPeriodPanel = () => {
    const { data } = useCustomer();
    const setBlackoutPeriod = useGraphqlMutation(muts.setBlackoutPeriod);

    async function handleSave() {
        if (form.validate().hasErrors) return;
        await notify.confirm("Are you sure you want to set the blackout period for this customer?", {
            title: "Confirm",
        });

        await setBlackoutPeriod.mutateAsync({ params: zBlackoutPeriod.parse(form.getValues()) });
        notify.show.success("Blackout period set.");
        await qk.invalidate("customer");

        modals.closeAll();
    }

    async function handleClear() {
        await notify.confirm("Are you sure you want to clear the blackout period for this customer?", {
            title: "Confirm",
        });

        await setBlackoutPeriod.mutateAsync({ params: null });
        notify.show.success("Blackout period cleared.");
        await qk.invalidate("customer");

        modals.closeAll();
    }

    const z24HrTimeStr = z.string().refine((it) => {
        if (it.split(":").length != 2) return false;
        return it.split(":").every((part) => !Number.isNaN(Number.parseInt(part)) && part.length == 2);
    });

    const zMinutesPastMidnightFrom24HrTimeStr = z24HrTimeStr.transform((it) => {
        const [hrs, min] = z.tuple([z.coerce.number().int().min(0).max(23), z.coerce.number().int().min(0).max(59)]).parse(it.split(":"));
        return hrs * 60 + min;
    });

    const zBlackoutPeriod = z.object({
        start: zMinutesPastMidnightFrom24HrTimeStr,
        end: zMinutesPastMidnightFrom24HrTimeStr,
    });

    const z24HrTimeStrFromMinutesPastMidnight = z.number().transform(
        (it) =>
            `${Math.floor(it / 60)
                .toString()
                .padStart(2, "0")}:${Math.floor(it % 60)
                .toString()
                .padStart(2, "0")}`,
    );

    const form = useForm({
        initialValues:
            z
                .object({ start: z24HrTimeStrFromMinutesPastMidnight, end: z24HrTimeStrFromMinutesPastMidnight })
                .nullable()
                .parse(data.blackoutPeriod) ?? undefined,
        validate: zodResolver(zBlackoutPeriod),
    });

    return (
        <Stack>
            <TimeInput label="Start Time" {...form.getInputProps("start")} />
            <TimeInput label="End Time" {...form.getInputProps("end")} />
            <Group justify="space-between">
                <Button variant="default" onClick={() => modals.closeAll()}>
                    Cancel
                </Button>
                <Group>
                    <Button variant="default" onClick={voiding(handleClear)}>
                        Clear
                    </Button>
                    <Button onClick={voiding(handleSave)}>Set</Button>
                </Group>
            </Group>
        </Stack>
    );
};

const muts = {
    setBlackoutPeriod: graphql(`
        mutation DevtoolsCustomerSetBlackoutPeriod($params: CustomerBlackoutPeriodInfoInput) {
            setCustomerBlackoutPeriod(params: $params) {
                id
            }
        }
    `),
    setDynamicTemplateId: graphql(`
        mutation DevtoolsCustomerSetDynamicTemplateId($id: String) {
            support {
                setDynamicTemplateId(dynamicTemplateId: $id)
            }
        }
    `),
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/customer/")({
    component: Page,
});
